/* eslint-disable */
import { ApContext } from './apcontext'
import {
  normalizePiece, MEM_TYPE_4, nRet, getKey, descSol, descPtoDiv2, descPtoDiv3, lIndex,
  standardPositionB1, standardPositionB2, standardPositionB3, standardPositionB4,
  standardPositionB5, standardPositionB6, standardPositionB7, standardPositionB8,
  standardPositionB9,
  B1, B2, B3, B4, B5, B6, B7, B8, B9,
  HOMOGENEOUS
} from './util'
import { newSet, constructRasterPoints } from './sets'
import { solve_BD } from './bd'
import { draw } from './draw'

// JS: N.B. Made functions lowercase start letter to not collide with JS constructor convention.

/******************************************************************
 ******************************************************************/
// inline int roundToNearest(double a) {
function roundToNearest (a) {
  return Math.floor(a + 0.5)
}

/******************************************************************
 ******************************************************************/

/**
 * Calculate the upper bound of a given rectangle R(x,y) (degenerated L).
 *
 * Parameters:
 * x - Length of the rectangle.
 *
 * y - Width of the rectangle.
 *
 * Return:
 * The computed upper bound.
 */
// inline int R_UpperBound(int x, int y) {
function r_UpperBound (inX, inY) {
  /* A(R) / lw */
  const x = ApContext.normalize[inX]
  const y = ApContext.normalize[inY]
  return ApContext.upperBound[ApContext.indexX[x]][ApContext.indexY[y]]
}

/******************************************************************
 ******************************************************************/

/**
 * Calculate the upper bound of a given L.
 *
 * Parameters:
 * q - The L-piece.
 *
 * Return:
 * The computed upper bound for this L-piece.
 */
// inline int L_UpperBound(int *q) {
function l_UpperBound (q) {
  /* Area(L) / lw */
  return Math.floor((q[0] * q[1] - (q[0] - q[2]) * (q[1] - q[3])) / (ApContext.l * ApContext.w))
}

/******************************************************************
 ******************************************************************/

/**
 * Calculate the lower bound of a given rectangle R(x,y) (degenerated L).
 *
 * Parameters:
 * x - Length of the rectangle.
 *
 * y - Width of the rectangle.
 *
 * Return:
 * The computed lower bound.
 */
// inline int R_LowerBound(int x, int y) {
function r_LowerBound (inX, inY) {
  const x = ApContext.normalize[inX]
  const y = ApContext.normalize[inY]
  return ApContext.lowerBound[ApContext.indexX[x]][ApContext.indexY[y]]
}

/******************************************************************
 ******************************************************************/

/**
 * Calculate the lower bound of a given L. It divides the L in two
 * rectangles and calculates their lower bounds to compose the lower
 * bound of the L-piece.
 *
 * +-----+              +-----+              +-----+
 * |     |              |     |              |     |
 * |     |              |     |              |     |
 * |     +----+   -->   +-----+----+    or   |     +----+
 * |          |         |          |         |     |    |
 * |          |         |          |         |     |    |
 * +----------+         +----------+         +-----+----+
 *                           (a)                  (b)
 *
 * Parameters:
 * q - The L-piece.
 *
 * Return:
 * The computed lower bound.
 */
// inline int L_LowerBound(int *q, bool *horizontalCut) {
// JS no pointer passing, returns map {horizontalCut: <bool>, value: <int>}.
function l_LowerBound (q) {
  const a = ApContext.lowerBound[
    ApContext.indexX[ApContext.normalize[q[2]]]][ApContext.indexY[ApContext.normalize[q[1] - q[3]]]] +
    ApContext.lowerBound[
      ApContext.indexX[ApContext.normalize[q[0]]]][ApContext.indexY[ApContext.normalize[q[3]]]]

  const b = ApContext.lowerBound[
    ApContext.indexX[ApContext.normalize[q[2]]]][ApContext.indexY[ApContext.normalize[q[1]]]] +
    ApContext.lowerBound[
      ApContext.indexX[ApContext.normalize[q[0] - q[2]]]][ApContext.indexY[ApContext.normalize[q[3]]]]

  return (a > b)
    ? { horizontalCut: true, lowerBound: a }
    : { horizontalCut: false, lowerBound: b }
}

/******************************************************************
 ******************************************************************/

/**
 * Divide an L-piece in two new L-pieces, according to the specified
 * subdivision, and normalize the two ones.
 *
 * Parameters:
 * i  - Point that determines the division int the L-piece.
 *
 * q  - The L-piece to be divided.
 *
 * q1 - It will store a new L-piece.
 *
 * q2 - It will store the other new L-piece.
 *
 * standardPosition - Pointer to the function that will divide the L-piece.
 */
// void divide(int *i, int *q, int *q1, int *q2,
//             void (*standardPosition)(int*, int*, int*, int*)) {
function divide (i, q, q1, q2, standardPosition) {
  /* Divide the L-piece in two new ones. */
  standardPosition(i, q, q1, q2)

  /* Normalize the new L-pieces. */
  normalizePiece(q1)
  normalizePiece(q2)
}

/******************************************************************
 ******************************************************************/

// /**
//  * Return the solution of the L-piece related to the index L.
//  *
//  * Parameters:
//  * L   - Index of the L-piece.
//  *
//  * key - Key for this L-piece.
//  *
//  * Return:
//  * The current solution of the specified L-piece.
//  */
// inline int getSolution(int L, int key) {
// JS: renamed from overloaded getSolution
// JS: Used by optimal() function which is unused in cpp code.
// function getSolutionByKey(L, key) {
//   return (ApContext.memory_type === MEM_TYPE_4)
//     ? ApContext.solution[L] & nRet
//     : ApContext.solutionMap[L][key] & nRet;
// }

/******************************************************************
 ******************************************************************/

/**
 * Return the solution of the L-piece related to the index L.
 *
 * Parameters:
 * L - Index of the L-piece.
 *
 * q - The L-piece.
 *
 * Returns:
 * The current solution of the specified L-piece.
 */
// inline int getSolution(int L, int *q) {
// JS: renamed from overloaded getSolution
function getSolutionInQ (L, q) {
  if (ApContext.memory_type === MEM_TYPE_4) {
    return ApContext.solution[L] & nRet
  } else {
    const key = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type)
    return ApContext.solutionMap[L][key] & nRet
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Return the solution of the L-piece related to the index L.
 *
 * Parameters:
 * L   - Index of the L-piece.
 *
 * q   - The L-piece.
 *
 * key - Key for this L-piece.
 *
 * Return:
 * The current solution of the specified L-piece.
 */
// inline int getSolution(int L, int *q, int *key) {
// JS: renamed from overloaded getSolution - returns map {key, solution}
function getSolutionAndKeyInQ (L, q, inKey) {
  if (ApContext.memory_type === MEM_TYPE_4) {
    return {
      solution: ApContext.solution[L],
      key: inKey
    }
  } else {
    const key = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type)
    return {
      solution: ApContext.solutionMap[L][key],
      key
    }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Store the solution of an L-piece.
 *
 * Parameters:
 * L         - Index of the L-piece.
 *
 * key       - Key for this L-piece.
 *
 * LSolution - Solution to be stored.
 *
 */
// inline void storeSolution(int L, int key, int LSolution) {
function storeSolution (L, key, LSolution) {
  if (ApContext.memory_type === MEM_TYPE_4) {
    ApContext.solution[L] = LSolution
  } else {
    ApContext.solutionMap[L][key] = LSolution
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Store the point where the division in the L-piece was made.
 *
 * Parameters:
 * L     - Index of the L-piece.
 *
 * key   - Key for this L-piece.
 *
 * point - Representation of the point where the division was made.
 *
 */
// inline void storeDivisionPoint(int L, int key, int point) {
function storeDivisionPoint (L, key, point) {
  if (ApContext.memory_type === MEM_TYPE_4) {
    ApContext.divisionPoint[L] = point
  } else {
    ApContext.divisionPointMap[L][key] = point
  }
}

/******************************************************************
 ******************************************************************/

// /**
//  * Verify whether the solution is optimal.
//  *
//  * Parameters:
//  * L          - Index of the L-piece.
//  *
//  * key        - Key for this L-piece.
//  *
//  * upperBound - Upper bound for the L-piece.
//  *
//  * Return:
//  * Return whether the current solution for the L-piece is optimal.
//  */
// inline bool optimal(int L, int key, int upperBound) {
// JS: Unused in cpp code.
// function optimal(L, key, upperBound) {
//   let Lsolution = getSolutionByKey(L, key);
//   return (Lsolution & nRet) === upperBound;
// }

/******************************************************************
 ******************************************************************/

/**
 * Divide the L-piece in every possible way, according to the specified
 * subdivision B.
 *
 * Parameters:
 * L                - Index of the L-piece.
 *
 * q                - The L-piece.
 *
 * constraints      - Constraints that determine the interval of x' and y'.
 *
 * B                - The subdivision.
 *
 * standardPosition - Pointer to the function that divides the L-piece
 *                    according to the subdivision B.
 *
 * X                - Set of raster points.
 *
 * startX           - Index to start the divisions on the set X.
 *
 * Y                - Set of raster points.
 *
 * startY           - Index to start the divisions on the set Y.
 */
// int divideL(int L, int *q, int *constraints, int B,
//             void (*standardPosition)(int*, int*, int*, int*),
//             Set X, int startX, Set Y, int startY) {
// todo-jr:: passed in Sets X and Y are by value here!
function divideL (L, q, constraints, B, standardPosition, X, startX, Y, startY) {
  /* i_k[0] <- x'
   * i_k[1] <- y'
   */
  // int i_k[2];
  // int i_x, i_y;
  // int q1[4], q2[4];
  const i_k = new Array(2).fill(0)
  let i_x, i_y
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0)

  // int key = 0;
  // int LSolution = getSolution(L, q, &key);
  // int upperBound = L_UpperBound(q);
  let { solution: LSolution, key } = getSolutionAndKeyInQ(L, q, 0)
  const upperBound = l_UpperBound(q)

  for (i_x = startX; i_x < X.size; i_x++) {
    i_k[0] = X.points[i_x]
    if (i_k[0] > constraints[1]) {
      break
    }

    for (i_y = startY; i_y < Y.size; i_y++) {
      i_k[1] = Y.points[i_y]
      if (i_k[1] > constraints[3]) {
        break
      }

      divide(i_k, q, q1, q2, standardPosition)
      if (q1[0] < 0 || q2[0] < 0) {
        continue
      }

      if (l_UpperBound(q1) + l_UpperBound(q2) > (LSolution & nRet)) {
        /* It is possible that this division gets a better solution. */
        // int L1 = LIndex(q1[0], q1[1], q1[2], q1[3], memory_type);
        // int L2 = LIndex(q2[0], q2[1], q2[2], q2[3], memory_type);
        // int L1Solution = solve(L1, q1);
        // int L2Solution = solve(L2, q2);
        const L1 = lIndex(q1[0], q1[1], q1[2], q1[3], ApContext.memory_type)
        const L2 = lIndex(q2[0], q2[1], q2[2], q2[3], ApContext.memory_type)
        const L1Solution = solve(L1, q1)
        const L2Solution = solve(L2, q2)

        if ((L1Solution & nRet) + (L2Solution & nRet) > (LSolution & nRet)) {
          /* A better solution was found. */
          LSolution = ((L1Solution & nRet) + (L2Solution & nRet)) |
            (B << descSol)
          storeSolution(L, key, LSolution)
          storeDivisionPoint(L, key, i_k[0] | (i_k[1] << descPtoDiv2))
          if ((LSolution & nRet) === upperBound) {
            return LSolution
          }
        }
      }
    }
  }
  return LSolution
}

/******************************************************************
 ******************************************************************/

/**
 * Divide the L-piece in every possible way, according to the B6
 * subdivision.
 *
 * +-------------+--------+
 * |             |        |
 * |   (x',y')   |   L2   |
 * |      o------o        |
 * |      |  (x'',y')     |
 * |  L1  |               |
 * |      |               |
 * +------+---------------+
 *
 * Parameters:
 * L - Index of the L-piece.
 *
 * q - The L-piece.
 *
 * X - Set of raster points.
 *
 * Y - Set of raster points.
 */
// int divideB6(int L, int *q, Set X, Set Y) {
function divideB6 (L, q, X, Y) {
  /* i_k[0] <- x'
   * i_k[1] <- y'
   * i_k[2] <- x''
   */
  const i_k = new Array(3).fill(0)
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0)
  let { solution: LSolution, key } = getSolutionAndKeyInQ(L, q, 0)
  const upperBound = r_UpperBound(q[0], q[1])

  let i = 0
  for (i_k[0] = X.points[i]; i < X.size; i++) {
    i_k[0] = X.points[i]

    let j = i
    for (i_k[2] = X.points[j]; j < X.size; j++) {
      i_k[2] = X.points[j]
      if (i_k[0] === 0 && i_k[2] === 0) {
        continue
      }

      let k = 0
      for (i_k[1] = Y.points[k]; k < Y.size; k++) {
        i_k[1] = Y.points[k]
        divide(i_k, q, q1, q2, standardPositionB6)
        if (q1[0] < 0 || q2[0] < 0) {
          continue
        }

        if (l_UpperBound(q1) + l_UpperBound(q2) > (LSolution & nRet)) {
          /* It is possible that this division gets a better solution. */
          const L1 = lIndex(q1[0], q1[1], q1[2], q1[3], ApContext.memory_type)
          const L2 = lIndex(q2[0], q2[1], q2[2], q2[3], ApContext.memory_type)
          const L1Solution = solve(L1, q1)
          const L2Solution = solve(L2, q2)

          if ((L1Solution & nRet) + (L2Solution & nRet) > (LSolution & nRet)) {
            /* A better solution was found. */
            LSolution = ((L1Solution & nRet) + (L2Solution & nRet)) |
              (B6 << descSol)
            storeSolution(L, key, LSolution)
            storeDivisionPoint(L, key, i_k[0] | (i_k[1] << descPtoDiv2) |
              (i_k[2] << descPtoDiv3))

            if ((LSolution & nRet) === upperBound) {
              return LSolution
            }
          }
        }
      }
    }
  }
  return LSolution
}

/******************************************************************
 ******************************************************************/

/**
 * Divide the L-piece in every possible way, according to the B7
 * subdivision.
 *
 * +-------------+
 * |             |
 * |   (x',y'')  |
 * |      o------+
 * |      |      |
 * |  L1  |  L2  |
 * |      |      |
 * +------o      |
 * |   (x',y')   |
 * |             |
 * |             |
 * +-------------+
 *
 * Parameters:
 * L - Index of the L-piece.
 *
 * q - The L-piece.
 *
 * X - Set of raster points.
 *
 * Y - Set of raster points.
 */
// int divideB7(int L, int *q, Set X, Set Y) {
function divideB7 (L, q, X, Y) {
  /* i_k[0] <- x'
   * i_k[1] <- y'
   * i_k[2] <- y''
   */
  const i_k = new Array(3).fill(0)
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0)
  let { solution: LSolution, key } = getSolutionAndKeyInQ(L, q, 0)
  const upperBound = r_UpperBound(q[0], q[1])

  let j = 0
  for (i_k[1] = Y.points[j]; j < Y.size; j++) {
    i_k[1] = Y.points[j]

    let k = j
    for (i_k[2] = Y.points[k]; k < Y.size; k++) {
      i_k[2] = Y.points[k]
      if (i_k[1] === 0 && i_k[2] === 0) {
        continue
      }

      let i = 0
      for (i_k[0] = X.points[i]; i < X.size; i++) {
        i_k[0] = X.points[i]
        divide(i_k, q, q1, q2, standardPositionB7)
        if (q1[0] < 0 || q2[0] < 0) {
          continue
        }

        if (l_UpperBound(q1) + l_UpperBound(q2) > (LSolution & nRet)) {
          /* It is possible that this division gets a better solution. */
          const L1 = lIndex(q1[0], q1[1], q1[2], q1[3], ApContext.memory_type)
          const L2 = lIndex(q2[0], q2[1], q2[2], q2[3], ApContext.memory_type)
          const L1Solution = solve(L1, q1)
          const L2Solution = solve(L2, q2)

          if (((L1Solution & nRet) + (L2Solution & nRet)) > (LSolution & nRet)) {
            /* A better solution was found. */
            LSolution = ((L1Solution & nRet) + (L2Solution & nRet)) |
              (B7 << descSol)
            storeSolution(L, key, LSolution)
            storeDivisionPoint(L, key, i_k[0] | (i_k[1] << descPtoDiv2) |
              (i_k[2] << descPtoDiv3))

            if ((LSolution & nRet) === upperBound) {
              return LSolution
            }
          }
        }
      }
    }
  }
  return LSolution
}

/******************************************************************
 ******************************************************************/

/**
 * Solve the problem of packing rectangular (l,w)-boxes into the
 * specified L-shaped piece.
 *
 * Parameters:
 * L - Index of the L-piece.
 *
 * q - The L-piece. q = {X, Y, x, y}.
 */
// int solve(int L, int *q) {
function solve (L, q) {
  let key = 0
  if (ApContext.memory_type === MEM_TYPE_4) {
    if (ApContext.solution[L] !== -1) {
      /* This problem has already been solved. */
      return ApContext.solution[L]
    }
  } else {
    key = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type)
    if (ApContext.solutionMap[L].count(key) > 0) {
      /* This problem has already been solved. */
      return ApContext.solutionMap[L][key]
    }
  }

  if (q[0] !== q[2]) {
    // bool horizontalCut;
    // int lowerBound = L_LowerBound(q, &horizontalCut);
    // int upperBound = L_UpperBound(q);
    // int LSolution  = lowerBound | (B1 << descSol);
    const { horizontalCut, lowerBound } = l_LowerBound(q)
    const upperBound = l_UpperBound(q)
    let LSolution = lowerBound | (B1 << descSol)

    if (horizontalCut) { storeDivisionPoint(L, key, 0 | (q[3] << descPtoDiv2)) } else { storeDivisionPoint(L, key, q[2] | (0 << descPtoDiv2)) }

    storeSolution(L, key, LSolution)

    /* Try to solve this problem with homogeneous packing (or other
     * better solution already computed). */
    if ((LSolution & nRet) !== upperBound) {
      /* It was not possible to solve this problem with homogeneous
       * packing. */
      // int constraints[4];
      // int startX = 0;
      // int startY = 0;
      const constraints = new Array(4).fill(0)
      let startX, startY

      /* Construct the raster points sets X and Y. */
      // Set X, Y;
      // constructRasterPoints(q[0], q[1], &X, &Y, normalSetX);
      const { rasterPointsX: X, rasterPointsY: Y } = constructRasterPoints(q[0], q[1], ApContext.normalSetX)
      // noinspection StatementWithEmptyBodyJS
      for (startX = 0; X.points[startX] < q[2]; startX++) ;
      // noinspection StatementWithEmptyBodyJS
      for (startY = 0; Y.points[startY] < q[3]; startY++) ;

      /***********************************
       * 0 <= x' <= x  and  0 <= y' <= y *
       ***********************************/
      constraints[0] = 0
      constraints[1] = q[2]
      constraints[2] = 0
      constraints[3] = q[3]

      /* B1 subdivision.
       *
       * +------------+
       * |            |
       * |            |(x,y)
       * |      +-----o-----+
       * |  L1  |           |
       * |      |     L2    |
       * +------o           |
       * |   (x',y')        |
       * |                  |
       * +------------------+
       */
      LSolution = divideL(L, q, constraints, B1, standardPositionB1, X, 0, Y, 0)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /* B3 subdivision.
       *
       * +------+-----+
       * |      |     |
       * |      |     |(x,y)
       * |      | L2  o-----+
       * |      |           |
       * |  L1  |           |
       * |      o-----------+
       * |   (x',y')        |
       * |                  |
       * +------------------+
       */
      LSolution = divideL(L, q, constraints, B3, standardPositionB3, X, 0, Y, 0)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /* B5 subdivision.
       *
       * +------------+
       * |            |
       * |     L1     |(x,y)
       * |            o-----+
       * |   (x',y')  |     |
       * |      o-----+     |
       * |      |           |
       * |      |     L2    |
       * |      |           |
       * +------+-----------+
       */
      LSolution = divideL(L, q, constraints, B5, standardPositionB5, X, 0, Y, 0)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /***********************************
       * 0 <= x' <= x  and  y <= y' <= Y *
       ***********************************/
      constraints[0] = 0
      constraints[1] = q[2]
      constraints[2] = q[3]
      constraints[3] = Y.points[Y.size - 1]

      /* B2 subdivision.
       *
       * +------------+
       * |            |
       * |   (x',y')  |
       * +------o     |
       * |      | L1  |
       * |      |     |(x,y)
       * |      +-----o-----+
       * |  L2              |
       * |                  |
       * +------------------+
       */
      LSolution = divideL(L, q, constraints, B2, standardPositionB2,
        X, 0, Y, startY)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /* B8 subdivision.
       *
       * +------------+
       * |            |
       * |   (x',y')  |
       * |      o-----+
       * |      |     |
       * |  L1  |     |(x,y)
       * |      |     o-----+
       * |      |  L2       |
       * |      |           |
       * +------+-----------+
       */
      LSolution = divideL(L, q, constraints, B8, standardPositionB8,
        X, 0, Y, startY)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /***********************************
       * x <= x' <= X  and  0 <= y' <= y *
       ***********************************/
      constraints[0] = q[2]
      constraints[1] = X.points[X.size - 1]
      constraints[2] = 0
      constraints[3] = q[3]

      /* B4 subdivision.
       *
       * +------+
       * |      |
       * |      |(x,y)
       * |      o-----------+
       * |  L1  |           |
       * |      |  (x',y')  |
       * |      +-----o     |
       * |            | L2  |
       * |            |     |
       * +------------+-----+
       */
      LSolution = divideL(L, q, constraints, B4, standardPositionB4,
        X, startX, Y, 0)
      if ((LSolution & nRet) === upperBound) {
        return LSolution
      }

      /* B9 subdivision.
       *
       * +---------+
       * |         |
       * |         |(x,y)
       * |   L1    o---+----+
       * |             |    |
       * |             |    |
       * +-------------o    |
       * |          (x',y') |
       * |     L2           |
       * |                  |
       * +------------------+
       */
      LSolution = divideL(L, q, constraints, B9, standardPositionB9,
        X, startX, Y, 0)
    }
    return LSolution
  } /* if q[0] != q[2] */
  else {
    /* Degenerated L (a rectangle) */

    let LSolution = r_LowerBound(q[0], q[1]) | (HOMOGENEOUS << descSol)
    const upperBound = r_UpperBound(q[0], q[1])
    storeSolution(L, key, LSolution)

    /* Verify whether it could not be solved with homogeneous packing. */
    if ((LSolution & nRet) !== upperBound) {
      /* Construct the raster points sets X and Y. */
      // Set X, Y;
      // constructRasterPoints(q[0], q[1], &X, &Y, normalSetX);
      const { rasterPointsX: X, rasterPointsY: Y } = constructRasterPoints(q[0], q[1], ApContext.normalSetX)

      /* Try the subdivisions B6 and B7. */

      /* B6 subdivision.
       *
       * +-------------+--------+
       * |             |        |
       * |   (x',y')   |   L2   |
       * |      o------o        |
       * |      |  (x'',y')     |
       * |  L1  |               |
       * |      |               |
       * +------+---------------+
       */
      LSolution = divideB6(L, q, X, Y)
      if ((LSolution & nRet) === upperBound) {
        /* Update the lower bound for this rectangular piece. */
        ApContext.lowerBound[ApContext.indexX[q[0]]][ApContext.indexY[q[1]]] = LSolution & nRet

        return LSolution
      }

      /* B7 subdivision.
       *
       * +-------------+
       * |             |
       * |   (x',y'')  |
       * |      o------+
       * |      |      |
       * |  L1  |  L2  |
       * |      |      |
       * +------o      |
       * |   (x',y')   |
       * |             |
       * |             |
       * +-------------+
       */
      LSolution = divideB7(L, q, X, Y)

      /* Update the lower bound for this rectangular piece. */
      ApContext.lowerBound[ApContext.indexX[q[0]]][ApContext.indexY[q[1]]] = LSolution & nRet
    }
    return LSolution
  }
}

/******************************************************************
 ******************************************************************/

// void makeIndices(int L, int W) {
function makeIndices (L, W) {
  // Set X, Y, raster;
  // constructRasterPoints(L, W, &X, &Y, normalSetX);
  const { rasterPointsX: X, rasterPointsY: Y } = constructRasterPoints(L, W, ApContext.normalSetX)

  // int j = 0;
  // int k = 0;
  // int i = 0;
  let j = 0
  let k = 0
  let i = 0

  const raster = newSet(L + 2)

  while (i < X.size && X.points[i] <= L &&
  j < Y.size && Y.points[j] <= W) {
    if (X.points[i] === Y.points[j]) {
      raster.points[k++] = X.points[i++]
      raster.size++
      j++
    } else if (X.points[i] < Y.points[j]) {
      raster.points[k++] = X.points[i++]
      raster.size++
    } else {
      raster.points[k++] = Y.points[j++]
      raster.size++
    }
  }
  while (i < X.size && X.points[i] <= L) {
    if (X.points[i] > raster.points[k - 1]) {
      raster.points[k++] = X.points[i]
      raster.size++
    }
    i++
  }
  if (k > 0 && raster.points[k - 1] < L) {
    raster.points[k++] = L
    raster.size++
  }
  raster.points[k] = L + 1
  raster.size++

  // indexRasterX = new int[L + 2];
  // indexRasterY = new int[W + 2];
  ApContext.indexRasterX = new Array(L + 2).fill(0)
  ApContext.indexRasterY = new Array(W + 2).fill(0)

  j = 0
  ApContext.numRasterX = 0
  for (let i = 0; i <= L; i++) {
    if (raster.points[j] === i) {
      ApContext.indexRasterX[i] = ApContext.numRasterX++
      j++
    } else {
      ApContext.indexRasterX[i] = ApContext.indexRasterX[i - 1]
    }
  }
  ApContext.indexRasterX[L + 1] = ApContext.indexRasterX[L] + 1

  j = 0
  ApContext.numRasterY = 0
  for (let i = 0; i <= W; i++) {
    if (raster.points[j] === i) {
      ApContext.indexRasterY[i] = ApContext.numRasterY++
      j++
    } else {
      ApContext.indexRasterY[i] = ApContext.indexRasterY[i - 1]
    }
  }
  ApContext.indexRasterY[W + 1] = ApContext.indexRasterY[W] + 1
}

/******************************************************************
 ******************************************************************/
// bool tryAllocateMemory(int size) {
function tryAllocateMemory (size) {
  // todo-jr:: change all usages map in js has map.get(key) rather than map[key] and map.set(k, v) instead of assignment
  ApContext.solutionMap = new Array(size).fill(null).map(() => new Map())
  ApContext.divisionPointMap = new Array(size).map(() => new Map())
  return true
}

/******************************************************************
 ******************************************************************/
// void allocateMemory() {
function allocateMemory () {
  ApContext.memory_type = MEM_TYPE_4

  // int nL = roundToNearest((pow((double)numRasterX,
  //                              ceil((double)memory_type / 2.0)) *
  //                          pow((double)numRasterY,
  //                              floor((double)memory_type / 2.0))));
  // noinspection JSSuspiciousNameCombination
  const nL = roundToNearest(
    Math.pow(ApContext.numRasterX, Math.ceil(ApContext.memory_type / 2.0)) *
    Math.pow(ApContext.numRasterY, Math.floor(ApContext.memory_type / 2.0)))

  // solution = new int[nL];
  ApContext.solution = new Array(nL).fill(-1)
  // divisionPoint =  new int[nL];
  ApContext.divisionPoint = new Array(nL).fill(0)
  // for(let i = 0; i < nL; i++)
  //   ApContext.solution[i] = -1;

  tryAllocateMemory(nL)
}

/******************************************************************
 ******************************************************************/
// int runMain(int L_in, int W_in, int l_in, int w_in, const char *nativeStringSavePath) {
export function runMain (L_in, W_in, l_in, w_in) {
  if (L_in < W_in) {
    return { error: 'L < W' }
  }
  if (l_in < w_in) {
    return { error: 'l < w' }
  }

  // int L, W;
  const L = L_in
  const W = W_in
  ApContext.l = l_in
  ApContext.w = w_in
  // int q[4];
  // int BD_solution, L_solution;
  // int INDEX;
  // double BD_time, L_time;  // JS: ignored in JS
  // struct rusage usage, prev_usage;
  // int L_n, W_n;
  const q = new Array(4).fill(0)
  let BD_solution, L_solution
  let INDEX
  let L_n, W_n

  ApContext.memory_type = 5

  /* Try to solve the problem with Algorithm 1. */
  BD_solution = solve_BD(L, W, ApContext.l, ApContext.w, 0)

  L_solution = -1

  L_n = ApContext.normalize[L]
  W_n = ApContext.normalize[W]

  if (BD_solution !== ApContext.upperBound[ApContext.indexX[L_n]][ApContext.indexY[W_n]]) {
    /* The solution obtained by Algorithm 1 is not known to be
     * optimal. Then it will try to solve the problem with
     * L-Algorithm. */

    makeIndices(L_n, W_n)
    allocateMemory()

    q[0] = q[2] = L_n
    q[1] = q[3] = W_n

    INDEX = lIndex(q[0], q[1], q[2], q[3], ApContext.memory_type)
    solve(INDEX, q)
    L_solution = getSolutionInQ(INDEX, q)

    return {
      ...draw(L, W, INDEX, q, L_solution & nRet, true),
      info: {
        upperBound: ApContext.upperBound[ApContext.indexX[L_n]][ApContext.indexY[W_n]],
        BD_N: BD_solution,
        L_N: L_solution
      }
    }
  } else {
    q[0] = q[2] = L_n
    q[1] = q[3] = W_n
    return {
      ...draw(L, W, 0, q, BD_solution, false),
      'info': {
        upperBound: ApContext.upperBound[ApContext.indexX[L_n]][ApContext.indexY[W_n]],
        BD_N: BD_solution
      }
    }
  }

  // todo-jr:: also output auxillary info like optimality etc. logged with LOGD in cpp code
}
